import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircularProgress, Typography } from '@material-ui/core'
import { useHistory, useParams } from 'react-router'

import { useGetKnowledgeBaseEntityQuery } from 'api/queries/knowledgeBase/knowledgeBase'
import { useCompanyId } from 'providers/CompanyProvider'
import { KnowledgeBaseDisplayContentType } from 'types/KnowledgeBaseDisplayContentType'
import { getKnowledgeBaseDisplayContentType, getMimeTypeFromDownloadFilePath } from 'screens/KnowledgeBase/utils'
import { KnowledgeBaseDisplayContentFallback } from 'components/data/KnowledgeBaseDisplayContentFallback/KnowledgeBaseDisplayContentFallback'
import * as Styled from '../knowledgeBase.styles'

interface ParamTypes {
  id: string
}

const KnowledgeBaseDisplayContentScreen = (): JSX.Element => {
  const { t } = useTranslation()
  const { id } = useParams<ParamTypes>()
  const history = useHistory()
  const companyId = useCompanyId() || 0
  const didMount = useRef(false)
  const [isBroken, setIsBroken] = useState(false)

  const {
    data: knowledgeBaseData,
    isLoading,
    isError,
  } = useGetKnowledgeBaseEntityQuery(+id)

  useEffect(() => {
    if (didMount.current) {
      history.replace('/knowledge-base')
    } else {
      didMount.current = true
    }
  }, [companyId, history])

  const mimeType = getMimeTypeFromDownloadFilePath(knowledgeBaseData?.downloadFilePath || '')
  const contentFileType = getKnowledgeBaseDisplayContentType(mimeType)

  return (
    <Styled.DisplayRootContainer>
      <Styled.HeaderContainer>
        <Typography variant='h2'>
          {t('common.file')}{': '}{knowledgeBaseData?.name}
        </Typography>
      </Styled.HeaderContainer>
      {isError || isLoading ? (
        <Styled.HelpersContainer>
          {isError && <Typography color='error'>{t('common.errors.generic')}</Typography>}
          {isLoading && <CircularProgress color='primary' />}
        </Styled.HelpersContainer>
      ) : (
        <Styled.DisplayContentContainer>
          {contentFileType === KnowledgeBaseDisplayContentType.IMAGE && (
            <>
              {isBroken ? (
                <KnowledgeBaseDisplayContentFallback
                  downloadFilePath={knowledgeBaseData?.downloadFilePath || ''}
                />
              ) : (
                <Styled.ImageObject onError={() => setIsBroken(true)}>
                  <source
                    srcSet={knowledgeBaseData?.downloadFilePath || ''}
                    type={mimeType}
                  />
                  <Styled.Img alt='' />
                </Styled.ImageObject>
              )}
            </>
          )}
          {contentFileType === KnowledgeBaseDisplayContentType.VIDEO && (
            <>
              {isBroken ? (
                <KnowledgeBaseDisplayContentFallback
                  downloadFilePath={knowledgeBaseData?.downloadFilePath || ''}
                />
              ) : (
                <Styled.VideoObject controls onError={() => setIsBroken(true)}>
                  <source
                    src={knowledgeBaseData?.downloadFilePath || ''}
                    type={mimeType}
                  />
                </Styled.VideoObject>
              )}
            </>
          )}
          {contentFileType === KnowledgeBaseDisplayContentType.AUDIO && (
            <>
              {isBroken ? (
                <KnowledgeBaseDisplayContentFallback
                  downloadFilePath={knowledgeBaseData?.downloadFilePath || ''}
                />
              ) : (
                <Styled.AudioObject controls onError={() => setIsBroken(true)}>
                  <source
                    src={knowledgeBaseData?.downloadFilePath || ''}
                    type={mimeType}
                  />
                </Styled.AudioObject>
              )}
            </>
          )}
          {contentFileType === KnowledgeBaseDisplayContentType.PDF && (
            <Styled.PdfObject data={knowledgeBaseData?.downloadFilePath || ''} type={mimeType}>
              <KnowledgeBaseDisplayContentFallback
                downloadFilePath={knowledgeBaseData?.downloadFilePath || ''}
              />
            </Styled.PdfObject>
          )}
          {contentFileType === KnowledgeBaseDisplayContentType.UNSUPPORTED && (
            <KnowledgeBaseDisplayContentFallback
              downloadFilePath={knowledgeBaseData?.downloadFilePath || ''}
            />
          )}
        </Styled.DisplayContentContainer>
      )}
    </Styled.DisplayRootContainer>
  )
}

export { KnowledgeBaseDisplayContentScreen }
